
import React from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

interface ResponsiveChartTooltipProps {
  active?: boolean;
  payload?: any[];
  label?: any;
  formatter?: (value: any, name?: string, props?: any) => [string, string];
  labelFormatter?: (label: any) => React.ReactNode;
  contentStyle?: React.CSSProperties;
}

const ResponsiveChartTooltip: React.FC<ResponsiveChartTooltipProps> = ({
  active,
  payload,
  label,
  formatter,
  labelFormatter,
  contentStyle = {}
}) => {
  const { isMobile, screenSize } = useIsMobile();

  if (!active || !payload || payload.length === 0) {
    return null;
  }

  const defaultStyle = {
    backgroundColor: 'white',
    padding: isMobile ? (screenSize === 'xs' ? '6px 10px' : '8px 12px') : '10px 14px',
    border: '1px solid #e5e7eb',
    borderRadius: '10px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
    fontSize: isMobile ? (screenSize === 'xs' ? '11px' : '12px') : '13px',
    transition: 'all 0.2s ease',
    maxWidth: isMobile ? '200px' : '300px',
    ...contentStyle
  };

  return (
    <div style={defaultStyle} className="animate-fadeIn backdrop-blur-sm bg-white/95">
      {labelFormatter && label !== undefined ? (
        <div style={{ fontWeight: 600, marginBottom: '6px', color: '#333' }} className="border-b pb-1.5 mb-2">
          {labelFormatter(label)}
        </div>
      ) : null}
      
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
        {payload.map((entry, index) => {
          // Ensure formatter always returns a tuple of [string, string]
          let formattedValue = entry.value;
          let formattedName = entry.name;
          
          if (formatter) {
            const result = formatter(entry.value, entry.name, entry);
            // Handle different return types from formatter
            if (Array.isArray(result)) {
              if (typeof result[0] === 'number') {
                formattedValue = String(result[0]);
              } else {
                formattedValue = result[0];
              }
              formattedName = result[1] || entry.name;
            } else if (typeof result === 'string') {
              formattedValue = result;
            }
          }
            
          return (
            <div 
              key={`tooltip-item-${index}`}
              style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '12px',
                padding: '2px 0'
              }}
              className="hover:bg-muted/20 px-1 py-0.5 rounded transition-colors"
            >
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '6px',
                color: '#4b5563'
              }}>
                <div
                  style={{ 
                    width: '10px', 
                    height: '10px', 
                    backgroundColor: entry.color || entry.fill || '#8884d8',
                    borderRadius: '3px',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                  }}
                />
                <span>{formattedName}</span>
              </div>
              <span style={{ fontWeight: 600 }}>{formattedValue}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ResponsiveChartTooltip;
